import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Stack,
  Typography,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Chip,
  alpha,
  useTheme,
  Tooltip,
  Skeleton,
  Paper
} from '@mui/material';
import {
  ExpandMore,
  AddCircleOutline,
  Edit,
  DeleteOutline,
  ContentCopy,
  MeetingRoom,
  PeopleOutline,
  School as SchoolIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';

import { SectionInstance, SessionClass } from '../../types/sessions';
import { sectionInstancesApi } from '../../services/sessions/sectionInstancesApi';
import { classesApi } from '../../services/sessions/classesApi';
import { useModulePermissions } from '../../hooks/useModulePermissions';

import SectionFormDialog from './SectionFormDialog';
import CopySectionsDialog from './CopySectionsDialog';
import ConfirmationDialog from '../common/ConfirmationDialog';
import EmptyState from '../common/EmptyState';

interface SectionsTabProps {
  sessionId: string;
}

const SectionsTab: React.FC<SectionsTabProps> = ({ sessionId }) => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const { canWrite } = useModulePermissions();
  const writable = canWrite('sections');

  const [classes, setClasses] = useState<SessionClass[]>([]);
  const [sections, setSections] = useState<SectionInstance[]>([]);
  const [loading, setLoading] = useState(true);

  const [formOpen, setFormOpen] = useState(false);
  const [editingSection, setEditingSection] = useState<SectionInstance | null>(null);
  const [formClass, setFormClass] = useState<SessionClass | null>(null);

  const [copyOpen, setCopyOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<SectionInstance | null>(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cls, secs] = await Promise.all([
        classesApi.list(),
        sectionInstancesApi.list({ sessionId })
      ]);
      setClasses(cls);
      setSections(secs);
    } catch (err: any) {
      enqueueSnackbar(err?.message || 'Could not load sections', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [sessionId, enqueueSnackbar]);

  useEffect(() => { load(); }, [load]);

  // Optimistic update helpers — applied after a successful API call so the
  // page never visually reloads. Accordion expand state + scroll position
  // stay exactly where the user left them.
  const upsertSection = useCallback((saved: SectionInstance) => {
    setSections(prev => {
      const idx = prev.findIndex(s => s._id === saved._id);
      if (idx === -1) return [...prev, saved];
      const next = prev.slice();
      next[idx] = saved;
      return next;
    });
  }, []);
  const removeSection = useCallback((id: string) => {
    setSections(prev => prev.filter(s => s._id !== id));
  }, []);

  const sectionsByClass = useMemo(() => {
    const map = new Map<string, SectionInstance[]>();
    for (const s of sections) {
      const key = String(s.classId);
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(s);
    }
    return map;
  }, [sections]);

  // Section names that exist in this session — used by the form dialog as
  // freeSolo autocomplete suggestions. Excludes the names already in the
  // currently-open class (those can't be re-added there anyway).
  const nameSuggestions = useMemo(() => {
    if (!formClass) return Array.from(new Set(sections.map(s => s.name))).sort();
    const takenInThisClass = new Set(
      (sectionsByClass.get(String(formClass._id)) ?? []).map(s => s.name)
    );
    const otherNames = sections
      .filter(s => String(s.classId) !== String(formClass._id))
      .map(s => s.name);
    return Array.from(new Set(otherNames))
      .filter(n => !takenInThisClass.has(n))
      .sort();
  }, [sections, sectionsByClass, formClass]);

  const openAdd = (cls: SessionClass) => {
    setFormClass(cls);
    setEditingSection(null);
    setFormOpen(true);
  };
  const openEdit = (cls: SessionClass, section: SectionInstance) => {
    setFormClass(cls);
    setEditingSection(section);
    setFormOpen(true);
  };

  const requestDelete = (section: SectionInstance) => setDeleteTarget(section);

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await sectionInstancesApi.remove(deleteTarget._id);
      enqueueSnackbar('Section deleted', { variant: 'success' });
      removeSection(deleteTarget._id);
      setDeleteTarget(null);
    } catch (err: any) {
      enqueueSnackbar(err?.message || 'Could not delete section', { variant: 'error' });
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <Stack spacing={1}>
        <Skeleton variant="rounded" height={56} />
        <Skeleton variant="rounded" height={56} />
        <Skeleton variant="rounded" height={56} />
      </Stack>
    );
  }

  if (classes.length === 0) {
    return (
      <EmptyState
        dense
        icon={<SchoolIcon />}
        title="No classes defined yet"
        description="Add classes from Administration → Classes first; sections live within them."
      />
    );
  }

  return (
    <Box>
      {/* Toolbar */}
      <Stack direction="row" justifyContent="flex-end" sx={{ mb: 2 }}>
        {writable && (
          <Button
            variant="text"
            startIcon={<ContentCopy fontSize="small" />}
            onClick={() => setCopyOpen(true)}
            sx={{ color: 'text.secondary' }}
          >
            Copy from another session
          </Button>
        )}
      </Stack>

      <Stack spacing={1}>
        {classes.map(cls => {
          const classSections = sectionsByClass.get(String(cls._id)) ?? [];
          const isEmpty = classSections.length === 0;
          return (
            <Accordion
              key={cls._id}
              defaultExpanded={classSections.length > 0}
              disableGutters
              elevation={0}
              sx={{
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: '8px !important',
                '&:before': { display: 'none' },
                overflow: 'hidden'
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMore fontSize="small" />}
                sx={{
                  px: 1.5,
                  minHeight: 44,
                  '& .MuiAccordionSummary-content': { my: 0.75 }
                }}
              >
                <Stack direction="row" alignItems="center" spacing={1.25} sx={{ width: '100%' }}>
                  <SchoolIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                  <Typography variant="body2" fontWeight={600}>{cls.name}</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                    {isEmpty
                      ? 'no sections'
                      : `${classSections.length} section${classSections.length === 1 ? '' : 's'}`}
                  </Typography>
                  <Box sx={{ flex: 1 }} />
                  {classSections.length > 0 && (
                    <Chip
                      size="small"
                      label={classSections.length}
                      variant="outlined"
                      sx={{ height: 20, mr: 1, fontSize: 11, fontWeight: 600 }}
                    />
                  )}
                </Stack>
              </AccordionSummary>
              <AccordionDetails sx={{ px: 1.5, pt: 0, pb: 1.5 }}>
                {classSections.length > 0 ? (
                  <Stack spacing={0.5}>
                    {classSections.map(section => (
                      <Box
                        key={section._id}
                        sx={{
                          display: 'flex', alignItems: 'center',
                          px: 1, py: 0.75, borderRadius: 1,
                          '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.04) }
                        }}
                      >
                        <Box
                          sx={{
                            minWidth: 28, height: 24, px: 0.75, borderRadius: 0.75,
                            bgcolor: alpha(theme.palette.primary.main, 0.10),
                            color: theme.palette.primary.dark,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontWeight: 600, fontSize: '0.75rem',
                            mr: 1.25
                          }}
                        >
                          {section.name}
                        </Box>
                        <Stack direction="row" spacing={1.75} sx={{ flex: 1, color: 'text.secondary' }}>
                          {section.room && (
                            <Stack direction="row" spacing={0.4} alignItems="center">
                              <MeetingRoom sx={{ fontSize: 14 }} />
                              <Typography variant="caption">{section.room}</Typography>
                            </Stack>
                          )}
                          {section.capacity != null && (
                            <Stack direction="row" spacing={0.4} alignItems="center">
                              <PeopleOutline sx={{ fontSize: 14 }} />
                              <Typography variant="caption">Cap. {section.capacity}</Typography>
                            </Stack>
                          )}
                        </Stack>
                        {writable && (
                          <Stack direction="row" spacing={0.25}>
                            <Tooltip title="Edit section"><IconButton size="small" onClick={() => openEdit(cls, section)}><Edit sx={{ fontSize: 16 }} /></IconButton></Tooltip>
                            <Tooltip title="Delete section"><IconButton size="small" onClick={() => requestDelete(section)}><DeleteOutline sx={{ fontSize: 16 }} /></IconButton></Tooltip>
                          </Stack>
                        )}
                      </Box>
                    ))}
                  </Stack>
                ) : (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', px: 1, py: 0.5 }}>
                    No sections — students will be enrolled directly into <strong>{cls.name}</strong>. That's fine if it doesn't split.
                  </Typography>
                )}

                {writable && (
                  <Button
                    size="small"
                    startIcon={<AddCircleOutline sx={{ fontSize: 16 }} />}
                    onClick={() => openAdd(cls)}
                    sx={{ mt: 0.5, ml: 0.5, textTransform: 'none' }}
                  >
                    Add section
                  </Button>
                )}
              </AccordionDetails>
            </Accordion>
          );
        })}
      </Stack>

      <SectionFormDialog
        open={formOpen}
        sessionId={sessionId}
        classId={formClass?._id ?? ''}
        className={formClass?.name}
        initial={editingSection}
        nameSuggestions={nameSuggestions}
        onClose={() => setFormOpen(false)}
        onSaved={upsertSection}
      />

      <CopySectionsDialog
        open={copyOpen}
        targetSessionId={sessionId}
        onClose={() => setCopyOpen(false)}
        onCopied={load}
      />

      <ConfirmationDialog
        open={Boolean(deleteTarget)}
        title="Delete section?"
        message={
          deleteTarget
            ? `Section "${deleteTarget.name}" will be removed from this session's class. This is only possible if no students are enrolled in it.`
            : ''
        }
        confirmText="Delete section"
        confirmColor="error"
        loading={deleting}
        onClose={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
      />
    </Box>
  );
};

export default SectionsTab;
